/**
 * 界面局部状态（不缓存服务端数据）：
 * - scene：当前页面与对象，随消息发给导师作为“运行时上下文”；
 * - draft：示例语句只填入输入框、不自动发送；
 * - 导师：宽屏常驻右栏（tutorOpen），窄屏是抽屉（drawerOpen）；同一时刻只挂载一个导师面板；
 * - 阅读时：正在读的书、位置、屏幕上的文字、选中的内容也在 scene 里；阅读器另外登记“截当前页”的方法，
 *   附图在发问前暂存在 attachments；
 * - 当前对话 id 与主题记在本机浏览器里（每本书一个对话）。
 */
import { useEffect, useSyncExternalStore } from "react";
import { create } from "zustand";

export type Scene = {
  page: string;
  source_id?: string;
  source_title?: string;
  /** 章节 */
  location?: string;
  /** 屏幕上的文字（节选） */
  view_text?: string;
  /** 选中的原文 */
  selection?: string;
};

/** 一张待发送的图：data URL + 说明。 */
export type Attachment = { url: string; label: string };

type UiState = {
  scene: Scene;
  draft: string;
  draftNonce: number;
  /** 宽屏：导师右栏是否展开 */
  tutorOpen: boolean;
  /** 窄屏：导师抽屉是否打开 */
  drawerOpen: boolean;
  conversationId: string;
  paletteOpen: boolean;
  theme: "system" | "light" | "dark";
  /** 阅读器登记的“截当前页”：返回当前页的图（PDF 是整页，EPUB 是屏幕上的插图与公式图） */
  captureView: (() => Promise<Attachment[]>) | null;
  /** 下一次发问要带上的图 */
  attachments: Attachment[];
  setCaptureView: (fn: (() => Promise<Attachment[]>) | null) => void;
  addAttachments: (items: Attachment[]) => void;
  clearAttachments: () => void;
  setScene: (scene: Scene) => void;
  /** 阅读器里的位置、选区变化：只更新这几项，不换页面 */
  patchScene: (patch: Partial<Scene>) => void;
  fillDraft: (text: string) => void;
  setTutorOpen: (open: boolean) => void;
  setDrawerOpen: (open: boolean) => void;
  /** 打开导师：宽屏展开右栏，窄屏打开抽屉 */
  showTutor: () => void;
  setConversationId: (id: string) => void;
  setPaletteOpen: (open: boolean) => void;
  setTheme: (theme: "system" | "light" | "dark") => void;
};

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* 浏览器存储不可用时只在本次会话生效 */
  }
}

export const WIDE = "(min-width: 1280px)";

function isWide(): boolean {
  return typeof window !== "undefined" && window.matchMedia(WIDE).matches;
}

export function newConversationId(): string {
  return `cnv_${crypto.randomUUID().replaceAll("-", "").slice(0, 24)}`;
}

/** 订阅媒体查询（布局切换用）。 */
export function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", notify);
      return () => list.removeEventListener("change", notify);
    },
    () => window.matchMedia(query).matches,
  );
}

/** 当前对话：刷新后接着同一个对话（服务端历史为准）。 */
function initialConversation(): string {
  const saved = read("zx-conversation");
  if (saved) return saved;
  const id = newConversationId();
  write("zx-conversation", id);
  return id;
}

export const useUi = create<UiState>((set) => ({
  scene: { page: "首页" },
  draft: "",
  draftNonce: 0,
  tutorOpen: read("zx-tutor") !== "closed",
  drawerOpen: false,
  conversationId: initialConversation(),
  paletteOpen: false,
  theme: (read("zx-theme") as UiState["theme"] | null) ?? "system",
  captureView: null,
  attachments: [],
  setCaptureView: (captureView) => set({ captureView }),
  addAttachments: (items) =>
    set((s) => ({
      attachments: [
        ...s.attachments,
        ...items.filter((i) => !s.attachments.some((a) => a.url === i.url)),
      ].slice(0, 8),
    })),
  clearAttachments: () => set({ attachments: [] }),
  setScene: (scene) => set({ scene }),
  patchScene: (patch) => set((s) => ({ scene: { ...s.scene, ...patch } })),
  fillDraft: (draft) =>
    set((s) => ({
      draft,
      draftNonce: s.draftNonce + 1,
      ...(isWide() ? { tutorOpen: true } : { drawerOpen: true }),
    })),
  setTutorOpen: (tutorOpen) => {
    write("zx-tutor", tutorOpen ? null : "closed");
    set({ tutorOpen });
  },
  setDrawerOpen: (drawerOpen) => set({ drawerOpen }),
  showTutor: () => (isWide() ? useUi.getState().setTutorOpen(true) : set({ drawerOpen: true })),
  setConversationId: (conversationId) => {
    write("zx-conversation", conversationId);
    set({ conversationId });
  },
  setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
  setTheme: (theme) => {
    write("zx-theme", theme);
    set({ theme });
  },
}));

/** 页面声明自己的场景：导师据此知道你在看什么。 */
export function useScene(scene: Scene) {
  const setScene = useUi((s) => s.setScene);
  const key = JSON.stringify(scene);
  // biome-ignore lint/correctness/useExhaustiveDependencies: 以序列化后的场景为依赖
  useEffect(() => {
    setScene(scene);
  }, [key, setScene]);
}

/** 每本书一个对话：在书里问的问题接着这本书的上下文。 */
export function bookConversationId(sourceId: string): string {
  return `cnv_book_${sourceId.replace(/^src_/, "")}`;
}
